import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Eye, EyeOff,Brush, AlertCircle, RefreshCw, X, AlertTriangle, CheckCircle, XCircle, ChevronLeft, Download, Upload, ChevronRight, Image as ImageIcon, ZoomIn, Loader2 } from 'lucide-react';
import { useAuth, authenticatedFetch } from '@/contexts/AuthContext';
import MaskAnnotator from '@/components/MaskAnnotationDialog';

import ReadinessBar from '@/components/reviews/ReadinessBar';
import config from '@/config';

const imageCache = new Map();

const preloadImages = (urls) => {
  urls.forEach(url => {
    if (url && !imageCache.has(url)) {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Handle CORS
      
      img.onload = () => {
        imageCache.set(url, img);
        console.log('✓ Preloaded:', url);
      };
      
      img.onerror = (e) => {
        console.error('✗ Failed to preload:', url, e);
      };
      
      img.src = url;
    }
  });
};
function ManualLensSubstitution({ onBack, onComplete, item, authenticatedFetch, user }) {
  const [loading, setLoading] = useState(false);
  const [nodeId, setNodeId] = useState(null);
  const [images, setImages] = useState([]);
  const [downloadedFlags, setDownloadedFlags] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/lens-substitution/init/${item.id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      
      if (!response.ok) throw new Error('Failed to fetch images');
      
      const data = await response.json();
      setNodeId(data.node_id);
      setImages(data.images.map(img => ({ id: img.id, link: img.link, file: null })));
      setDownloadedFlags(new Array(data.images.length).fill(false));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (index) => {
    try {
      const image = images[index];
      const response = await fetch(image.link);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lens-image-${index + 1}.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      const newFlags = [...downloadedFlags];
      newFlags[index] = true;
      setDownloadedFlags(newFlags);
    } catch (err) {
      setError('Failed to download image');
    }
  };

  const handleUpload = async (index, file) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      // Add user_id as query parameter. BACKEND_API_ADDRESS is same-origin
      // ('/api'), so resolve against window.location.origin - new URL() with a
      // bare path and no base throws "Failed to construct 'URL'".
      const url = new URL(
        `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/lens-substitution/upload`,
        window.location.origin
      );
      url.searchParams.append('user_id', user.id);

      const response = await authenticatedFetch(url.toString(), {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) throw new Error('Failed to upload image');
      
      const data = await response.json();
      const newImages = [...images];
      newImages[index] = { ...newImages[index], id: data.image_id, file };
      setImages(newImages);
    } catch (err) {
      setError('Failed to upload image: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/lens-substitution/confirm`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            node_id: nodeId,
            image_ids: images.map(img => img.id),
          }),
        }
      );
      
      if (!response.ok) throw new Error('Failed to confirm substitution');
      
      // Success - close all dialogs
      if (onComplete) {
        onComplete();
      } else {
        onBack();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && images.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-red-600 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Loading images...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h4 className="font-semibold text-gray-900 mb-4">Manual Lens Substitution</h4>
      <p className="text-sm text-gray-600 mb-4">
        Download the images, make corrections, and upload the modified versions.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        </div>
      )}

      <div className="space-y-4 mb-4">
        {images.map((image, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <img
                  src={image.link}
                  alt={`Lens ${index + 1}`}
                  className="w-32 h-32 object-cover rounded border border-gray-300"
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 mb-1">Image {index + 1}</div>
                <div className="text-xs text-gray-500 mb-3 font-mono break-all">
                  {image.id}
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDownload(index)}
                    className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50 transition-colors flex items-center gap-1.5"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                  
                  <label
                    className={`px-3 py-1.5 rounded text-sm transition-colors flex items-center gap-1.5 ${
                      downloadedFlags[index]
                        ? 'bg-red-600 text-white hover:bg-red-700 cursor-pointer'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    {image.file ? 'Uploaded ✓' : 'Upload'}
                    <input
                      type="file"
                      accept="image/*"
                      disabled={!downloadedFlags[index] || loading}
                      onChange={(e) => e.target.files[0] && handleUpload(index, e.target.files[0])}
                      className="hidden"
                    />
                  </label>
                </div>
                
                {image.file && (
                  <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Replacement uploaded: {image.file.name}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          disabled={loading}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
        >
          Back
        </button>
        <button
          onClick={handleConfirm}
          disabled={loading}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Processing...
            </>
          ) : (
            'Confirm Substitution'
          )}
        </button>
      </div>
    </div>
  );
}

// Flatten the lens masks onto a transparent canvas the size of the frame image.
//
// The masks are stored as cutouts - one small image per lens, positioned by the
// percentages the lens-masks endpoint returns - so on their own they cannot be
// laid over the final image in an editor. Composited at the parent image's
// dimensions the result lines up pixel for pixel, which is the whole point:
// it can be dropped straight onto the downloaded final image as a layer.
async function buildLensMaskPng(masks, width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  for (const mask of masks) {
    const source = mask.mask;               // raw segmentation, not the red overlay
    const place = mask.placement;
    if (!source || !place) continue;        // unplaceable: drawing it would mislead

    // eslint-disable-next-line no-await-in-loop
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = source;
    });

    ctx.drawImage(
      img,
      (place.left_percent / 100) * width,
      (place.top_percent / 100) * height,
      (place.width_percent / 100) * width,
      (place.height_percent / 100) * height
    );
  }

  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

// The inverse of buildLensMaskPng: cut the edited full-size canvas back into
// one cutout per lens, which is how the masks are actually stored.
//
// Each lens is cropped from the region it was drawn into and written back at
// its original cutout dimensions, so the label keeps the size everything
// downstream expects - the insertion worker pairs a mask to a lens box by
// comparing cutout size against box size, and a mask that changed dimensions
// would stop pairing.
//
// Alpha is preserved rather than flattened to black and white: lens masks are
// stored RGBA with the lens opaque and the rest transparent.
async function sliceLensMasksFromPng(editedDataUrl, masks, width, height) {
  const edited = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = editedDataUrl;
  });

  // The annotator hands back a canvas at its own working size; map back onto
  // the parent's coordinate space before cropping.
  const scaleX = edited.width / width;
  const scaleY = edited.height / height;

  return masks
    .filter((m) => m.placement && m.cutout_size)
    .map((m) => {
      const place = m.placement;
      const canvas = document.createElement('canvas');
      canvas.width = m.cutout_size.width;
      canvas.height = m.cutout_size.height;
      const ctx = canvas.getContext('2d');

      ctx.drawImage(
        edited,
        (place.left_percent / 100) * width * scaleX,
        (place.top_percent / 100) * height * scaleY,
        (place.width_percent / 100) * width * scaleX,
        (place.height_percent / 100) * height * scaleY,
        0, 0, canvas.width, canvas.height
      );

      return { label_id: m.label_id, mask: canvas.toDataURL('image/png') };
    });
}

// Correct the lens mask against the final image itself.
//
// The other mask tools work on a single cutout in isolation, which is fine for
// judging a segmentation but no use for deciding whether a lens looks right in
// the delivered picture. This composites every lens mask onto the final image
// at full size, hands that to the editor, and slices the result back to the
// individual lenses on save.
function EditLensMaskModal({ onSubmit, onCancel, loading, item, authenticatedFetch, user }) {
  const [showAnnotator, setShowAnnotator] = useState(false);
  const [maskData, setMaskData] = useState(null);
  const [lensMasks, setLensMasks] = useState([]);
  const [parent, setParent] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const handleOpenAnnotator = async () => {
    setPreparing(true);
    setError(null);
    setNotice(null);
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/${item.id}/lens-masks`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      const size = data.parent_image;
      if (!size?.width || !size?.height) {
        throw new Error('this frame has no parent image size recorded');
      }
      const placed = (data.masks || []).filter(
        (m) => m.mask && m.placement && m.cutout_size
      );
      if (!placed.length) {
        throw new Error(
          data.masks?.length
            ? 'the masks for this frame could not be positioned'
            : 'this frame has no lens masks'
        );
      }
      if (placed.length < (data.lens_count || placed.length)) {
        setNotice(
          `Only ${placed.length} of ${data.lens_count} lenses could be placed; ` +
          'the others are not shown and will not be changed.'
        );
      }

      const blob = await buildLensMaskPng(placed, size.width, size.height);
      const asDataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });

      setLensMasks(placed);
      setParent(size);
      setMaskData(asDataUrl);
      setShowAnnotator(true);
    } catch (err) {
      setError(`Failed to open the mask editor: ${err.message}`);
    } finally {
      setPreparing(false);
    }
  };

  const handleAnnotatorComplete = async (result) => {
    // The editor is built around accept/reject; an untouched mask comes back as
    // 'accepted' and means "nothing to save".
    if (result.status !== 'rejected') {
      setShowAnnotator(false);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const slices = await sliceLensMasksFromPng(
        result.masks_data.mask, lensMasks, parent.width, parent.height
      );
      if (!slices.length) throw new Error('nothing could be sliced back out');

      const updateResponse = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/${item.id}/lens-masks/update`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            masks: slices,
            comment: result.comment || 'Lens mask corrected on the final image',
          }),
        }
      );
      if (!updateResponse.ok) {
        const detail = await updateResponse.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP ${updateResponse.status}`);
      }
      const saved = await updateResponse.json();

      setShowAnnotator(false);
      await onSubmit(
        'edit_lens_mask',
        `Lens mask corrected on the final image - ${saved.updated} lens(es)` +
        (result.comment ? ` - ${result.comment}` : '')
      );
    } catch (err) {
      setError(`Failed to save the mask: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (showAnnotator && maskData) {
    return (
      <div className="fixed inset-0 bg-white z-[70]">
        <MaskAnnotator
          imageUrl={item.final_image_url}
          maskData={maskData}
          tag="lens_mask"
          tagColor="#ef4444"
          review_lock_id={item.id}
          review_id={item.id}
          onComplete={handleAnnotatorComplete}
          containerMode={false}
        />
        <button
          onClick={() => setShowAnnotator(false)}
          disabled={submitting}
          className="fixed top-4 right-4 z-[80] p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        {submitting && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90]">
            <div className="bg-white rounded-lg p-6 flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-red-600" />
              <span className="text-sm text-gray-800">Saving the corrected mask…</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6">
      <h4 className="font-semibold text-gray-900 mb-4">Edit Mask</h4>
      <p className="text-sm text-gray-600 mb-4">
        Opens the lens mask over the final image at full size, so it can be
        corrected against what is actually delivered. Saving writes each lens
        back to its own mask.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        </div>
      )}
      {notice && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-900">
          {notice}
        </div>
      )}

      <div className="border border-gray-200 rounded-lg p-4 mb-4 flex items-start gap-4">
        <img
          src={item.final_image_url}
          alt="Final"
          className="w-32 h-32 object-cover rounded border border-gray-300 flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 mb-1">Final Image</div>
          <div className="text-xs text-gray-500 mb-3">{item.name || item.id}</div>
          <button
            onClick={handleOpenAnnotator}
            disabled={preparing || loading}
            className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
          >
            {preparing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brush className="w-4 h-4" />}
            {preparing ? 'Preparing…' : 'Open mask editor'}
          </button>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={loading || submitting}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function LogoInFinalImageModal({ onSubmit, onCancel, loading, item, authenticatedFetch, user }) {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [downloaded, setDownloaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [maskBusy, setMaskBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleDownloadLensMask = async () => {
    setMaskBusy(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/${item.id}/lens-masks`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      const parent = data.parent_image;
      if (!parent?.width || !parent?.height) {
        throw new Error('this frame has no parent image size recorded');
      }
      const placed = (data.masks || []).filter((m) => m.mask && m.placement);
      if (!placed.length) {
        throw new Error(
          data.masks?.length
            ? 'the masks for this frame could not be positioned'
            : 'this frame has no lens masks'
        );
      }

      const blob = await buildLensMaskPng(placed, parent.width, parent.height);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(item.name || item.id).replace(/\.[^.]+$/, '')}__lens-mask.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Say how many were drawn: a two-lens frame that yields one mask is worth
      // noticing before editing against it.
      if (placed.length < (data.lens_count || placed.length)) {
        setError(
          `Downloaded, but only ${placed.length} of ${data.lens_count} lenses could be placed.`
        );
      }
    } catch (err) {
      setError(`Failed to build the lens mask: ${err.message}`);
    } finally {
      setMaskBusy(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(item.final_image_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `final-image-${item.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setDownloaded(true);
    } catch (err) {
      setError('Failed to download image');
    }
  };

  const handleUpload = async (file) => {
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      // Same-origin base: see the note on the lens-substitution upload above.
      const url = new URL(
        `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/logo-fix/upload`,
        window.location.origin
      );
      url.searchParams.append('user_id', user.id);
      url.searchParams.append('item_id', item.id);
      
      const response = await authenticatedFetch(url.toString(), {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) throw new Error('Failed to upload image');
      
      const data = await response.json();
      setUploadedFile({ file, imageId: data.image_id });
    } catch (err) {
      setError('Failed to upload image: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!uploadedFile) {
      setError('Please upload the edited image first');
      return;
    }

    // Use the onSubmit callback with the logo_in_final_image reason
    await onSubmit('logo_in_final_image', `Logo removed - image ID: ${uploadedFile.imageId}`);
  };

  return (
    <div className="p-6">
      <h4 className="font-semibold text-gray-900 mb-4">Logo in Final Image</h4>
      <p className="text-sm text-gray-600 mb-4">
        Download the final image, remove the logo, and upload the corrected version.
        The lens mask is available at the same dimensions, so it can be used as a
        layer to edit the lens against.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        </div>
      )}

      <div className="border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <img
              src={item.final_image_url}
              alt="Final image"
              className="w-32 h-32 object-cover rounded border border-gray-300"
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 mb-1">Final Image</div>
            <div className="text-xs text-gray-500 mb-3">
              {item.name || item.id}
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={handleDownload}
                disabled={uploading}
                className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50 transition-colors flex items-center gap-1.5 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                Download
              </button>

              {/* Same dimensions as the final image, so it drops straight on as
                  a layer and the lens can be edited against it. */}
              <button
                onClick={handleDownloadLensMask}
                disabled={uploading || maskBusy}
                title="Lens mask as a PNG the same size as the final image"
                className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50 transition-colors flex items-center gap-1.5 disabled:opacity-50"
              >
                {maskBusy
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Download className="w-4 h-4" />}
                {maskBusy ? 'Building…' : 'Download lens mask'}
              </button>

              <label
                className={`px-3 py-1.5 rounded text-sm transition-colors flex items-center gap-1.5 ${
                  downloaded
                    ? 'bg-red-600 text-white hover:bg-red-700 cursor-pointer'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Upload className="w-4 h-4" />
                {uploadedFile ? 'Uploaded ✓' : uploading ? 'Uploading...' : 'Upload Edited'}
                <input
                  type="file"
                  accept="image/*"
                  disabled={!downloaded || uploading}
                  onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0])}
                  className="hidden"
                />
              </label>
            </div>
            
            {downloaded && (
              <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Image downloaded - you can now upload the edited version
              </div>
            )}
            
            {uploadedFile && (
              <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Edited image uploaded: {uploadedFile.file.name}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={loading || uploading}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          disabled={loading || uploading || !uploadedFile}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Processing...
            </>
          ) : (
            'Confirm Correction'
          )}
        </button>
      </div>
    </div>
  );
}

function FrameLensLipModal({ onSubmit, onCancel, loading, item, authenticatedFetch, user }) {
  const [subOption, setSubOption] = useState(null);
  const [details, setDetails] = useState('');
  
  if (!subOption) {
    // First step: Choose between reset or substitution
    return (
      <div className="p-6">
        <h4 className="font-semibold text-gray-900 mb-4">Frame Lens Lip Issue</h4>
        <p className="text-sm text-gray-600 mb-4">
          Abnormal lip detected on the internal side of the lens. Please select how to proceed:
        </p>
        
        <div className="space-y-3 mb-4">
          <button
            onClick={() => setSubOption('reset')}
            className="w-full text-left p-4 border-2 border-gray-200 hover:border-red-500 hover:bg-red-50 rounded-lg transition-colors"
          >
            <div className="flex items-center gap-3">
              <RefreshCw className="w-5 h-5 text-gray-700 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-medium text-gray-900 mb-1">Reset Lens Processing</div>
                <div className="text-xs text-gray-600">
                  Reset the workflow back to lens removal step and reprocess
                </div>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => setSubOption('fix')}
            className="w-full text-left p-4 border-2 border-gray-200 hover:border-red-500 hover:bg-red-50 rounded-lg transition-colors"
          >
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-gray-700 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-medium text-gray-900 mb-1">Manual Lens Substitution</div>
                <div className="text-xs text-gray-600">
                  Manually select and substitute the lens (requires manual intervention)
                </div>
              </div>
            </div>
          </button>
        </div>
        
        <button
          onClick={onCancel}
          className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
        >
          Cancel
        </button>
      </div>
    );
  }
  
  if (subOption === 'reset') {
    // Second step for reset: Confirm and add optional details
    return (
      <div className="p-6">
        <h4 className="font-semibold text-gray-900 mb-4">Reset Lens Processing</h4>
        <p className="text-sm text-gray-600 mb-4">
          This will reset the workflow back to the lens removal step and reprocess the entire lens pipeline.
        </p>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional notes (optional):
          </label>
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Add any additional details about the lens lip issue..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          />
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <strong>Warning:</strong> This will reset the lens removal node and all dependent nodes to pending status.
            </div>
          </div>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={() => setSubOption(null)}
            disabled={loading}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
          >
            Back
          </button>
          <button
            onClick={() => onSubmit('frame_lens_lip_reset', details || 'Reset lens processing due to lens lip issue')}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : 'Confirm Reset'}
          </button>
        </div>
      </div>
    );
  }
  
if (subOption === 'fix') {
  return (
    <ManualLensSubstitution 
      onBack={() => setSubOption(null)} 
      onComplete={onCancel}  // This will close all dialogs
      item={item}
      authenticatedFetch={authenticatedFetch}
      user={user}
    />
  );
}
  
  return null;
}



function NonTransparentLensesModal({ onSubmit, onCancel, loading }) {
  const [details, setDetails] = useState('');
  
  return (
    <div className="p-6">
      <h4 className="font-semibold text-gray-900 mb-4">Non Transparent Lenses</h4>
      <p className="text-sm text-gray-600 mb-4">
        The lenses are not transparent. Please provide additional details:
      </p>
      <textarea
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        placeholder="e.g., Lenses appear opaque, dark tint applied incorrectly..."
        rows={4}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-4"
      />
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={loading}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => onSubmit('non_transparent_lenses', details || 'Lenses are not transparent')}
          disabled={loading}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : 'Submit'}
        </button>
      </div>
    </div>
  );
}


function MaskErrorModal({ onSubmit, onCancel, loading }) {
  const [details, setDetails] = useState('');

  return (
    <div className="p-6">
      <h4 className="font-semibold text-gray-900 mb-4">Mask Error</h4>
      <p className="text-sm text-gray-600 mb-4">
        The lens segmentation mask is incorrect. This will reset the workflow from the lens segmentation step.
      </p>
      <textarea
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        placeholder="e.g., Mask is cutting into the frame, missing a region..."
        rows={4}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-4"
      />
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-800">
            <strong>Warning:</strong> This will reset the lens segmentation node and all dependent nodes to pending status.
          </div>
        </div>
      </div>
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={loading}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => onSubmit('mask_error', details || 'Lens segmentation mask error')}
          disabled={loading}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : 'Submit'}
        </button>
      </div>
    </div>
  );
}

function LensScaleErrorModal({ onSubmit, onCancel, loading }) {
  const [details, setDetails] = useState('');
  
  return (
    <div className="p-6">
      <h4 className="font-semibold text-gray-900 mb-4">Lens Scale Error</h4>
      <p className="text-sm text-gray-600 mb-4">
        The lens scale is incorrect. Please select the issue:
      </p>
      <textarea
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        placeholder="Optional: Add additional details about the scale issue..."
        rows={3}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-4"
      />
      <div className="flex flex-col gap-3">
        <div className="flex gap-3">
          <button
            onClick={() => onSubmit('lens_scale_error', `Lens too big${details ? ': ' + details : ''}`)}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : 'Lens Too Big'}
          </button>
          <button
            onClick={() => onSubmit('lens_scale_error', `Lens too small${details ? ': ' + details : ''}`)}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : 'Lens Too Small'}
          </button>
        </div>
        <button
          onClick={onCancel}
          disabled={loading}
          className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function OnlyOneLensCompleteModal({ onSubmit, onCancel, loading }) {
  const [details, setDetails] = useState('');
  
  return (
    <div className="p-6">
      <h4 className="font-semibold text-gray-900 mb-4">Only One Lens Complete</h4>
      <p className="text-sm text-gray-600 mb-4">
        Only one lens is complete. Please provide details:
      </p>
      <textarea
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        placeholder="e.g., Right lens is missing, left lens incomplete..."
        rows={4}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-4"
      />
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={loading}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => onSubmit('only_one_lens_complete', details || 'Only one lens is complete')}
          disabled={loading}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : 'Submit'}
        </button>
      </div>
    </div>
  );
}

function BboxEdgePaddingError({ onSubmit, onCancel, loading }) {
  const [details, setDetails] = useState('');
  
  return (
    <div className="p-6">
      <h4 className="font-semibold text-gray-900 mb-4">BBox edge padding error</h4>
      <p className="text-sm text-gray-600 mb-4">
        The padding of the bbox is not respected because the space around the frames is not enough
      </p>
      <textarea
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        placeholder="e.g., The bbox space is not enough..."
        rows={4}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-4"
      />
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={loading}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => onSubmit('bbox_lens_padding_error', details || 'Bbox edge spacing error')}
          disabled={loading}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : 'Submit'}
        </button>
      </div>
    </div>
  );
}

// Background Mask Mistake Modal with MaskAnnotator
function BackgroundMaskMistakeModal({ 
  onSubmit, 
  onCancel, 
  loading, 
  item, 
  authenticatedFetch, 
  user, 
  onMaskUpdated  // ADD THIS
}) {
  const [showAnnotator, setShowAnnotator] = useState(false);
  const [maskData, setMaskData] = useState(null);
  const [labelId, setLabelId] = useState(null);
  const [fetchingMask, setFetchingMask] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Convert alpha channel mask to black and white mask
  const convertAlphaMaskToBlackWhite = async (alphaDataUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        // Create canvas with same dimensions
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        
        // Draw the alpha mask
        ctx.drawImage(img, 0, 0);
        
        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Convert: if alpha > 128, make white (255,255,255), else black (0,0,0)
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3];
          const isWhite = alpha > 128;
          
          data[i] = isWhite ? 255 : 0;     // R
          data[i + 1] = isWhite ? 255 : 0; // G
          data[i + 2] = isWhite ? 255 : 0; // B
          data[i + 3] = 255;               // A (fully opaque)
        }
        
        // Put the modified data back
        ctx.putImageData(imageData, 0, 0);
        
        // Convert to base64
        const blackWhiteDataUrl = canvas.toDataURL('image/png');
        resolve(blackWhiteDataUrl);
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load mask image for conversion'));
      };
      
      img.src = alphaDataUrl;
    });
  };

  const handleOpenAnnotator = async () => {
    setFetchingMask(true);
    setError(null);
    
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/${item.id}/background-mask`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch background mask');
      }

      const data = await response.json();
      setMaskData(data.mask);
      setLabelId(data.label_id);
      setShowAnnotator(true);
    } catch (err) {
      console.error('Error fetching background mask:', err);
      setError('Failed to load background mask: ' + err.message);
    } finally {
      setFetchingMask(false);
    }
  };

  const handleAnnotatorComplete = async (result) => {
    if (result.status !== 'rejected') {
      // If accepted without changes, just close
      setShowAnnotator(false);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Convert the alpha channel mask to black and white
      console.log('Converting alpha mask to black and white...');
      const blackWhiteMask = await convertAlphaMaskToBlackWhite(result.masks_data.mask);
      console.log('Conversion complete');

      // 1. Update the mask data via backend endpoint with converted mask
      const updateResponse = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/background-mask/update`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            label_id: labelId,
            mask_data: blackWhiteMask,
            comment: result.comment || 'Background mask corrected'
          }),
        }
      );

      if (!updateResponse.ok) {
        throw new Error('Failed to update background mask');
      }

      console.log('Background mask updated successfully');

      // NEW: Call the refresh callback to update the mask display
      if (onMaskUpdated) {
        await onMaskUpdated();
      }

      // 2. Now submit the rejection using the simple reject endpoint
      const rejectResponse = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/reject/${item.id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            reason: `Background mask corrected - Label ID: ${labelId}${result.comment ? ` - ${result.comment}` : ''}` 
          }),
        }
      );

      if (!rejectResponse.ok) {
        throw new Error('Failed to submit rejection');
      }

      console.log('Rejection submitted successfully');

      // 3. Close annotator and complete the process
      setShowAnnotator(false);
      
      // 4. Complete the rejection flow (this will close the modal and move to next)
      onCancel(); // This will trigger the parent to refresh/move to next
      
    } catch (err) {
      console.error('Error processing mask correction:', err);
      setError('Failed to process correction: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseAnnotator = () => {
    setShowAnnotator(false);
    setMaskData(null);
    setLabelId(null);
  };

  if (showAnnotator && maskData) {
    return (
      <div className="fixed inset-0 bg-white z-[70]">
        <MaskAnnotator
          imageUrl={item.final_image_url}
          maskData={maskData}
          tag="background_mask"
          tagColor="#ef4444"
          review_lock_id={item.id}
          review_id={labelId}
          onComplete={handleAnnotatorComplete}
          containerMode={false}
        />
        <button
          onClick={handleCloseAnnotator}
          className="fixed top-4 right-4 z-[80] p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
          disabled={submitting}
        >
          <X className="w-5 h-5" />
        </button>
        {submitting && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]">
            <div className="bg-white rounded-lg p-6 flex items-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
              <span className="text-gray-900">Converting and saving mask...</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6">
      <h4 className="font-semibold text-gray-900 mb-4">Background Mask Mistake</h4>
      <p className="text-sm text-gray-600 mb-4">
        The background mask is incorrect. Click below to open the mask editor and correct the mask.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <strong>Instructions:</strong>
            <ul className="mt-2 space-y-1 list-disc list-inside">
              <li>Click "Open Mask Editor" to edit the background mask</li>
              <li>Use the brush tools to correct the mask</li>
              <li>Press "Reject Image" in the editor to save changes and submit</li>
              <li>Press "Accept Image" if no changes are needed (will cancel rejection)</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={loading || fetchingMask || submitting}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleOpenAnnotator}
          disabled={loading || fetchingMask || submitting}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {fetchingMask ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              <Brush className="w-4 h-4" />
              Open Mask Editor
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// Image Review Dialog with Mask Overlay
function ImageReviewDialog({ isOpen, onClose, item, onNext, onPrevious, hasNext, hasPrevious }) {
  const { authenticatedFetch, user } = useAuth();
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchingReasons, setFetchingReasons] = useState(false);
  const [maskData, setMaskData] = useState(null);
  const [showMask, setShowMask] = useState(true);
  const [fetchingMask, setFetchingMask] = useState(false);
  const [lensMasks, setLensMasks] = useState([]);
  const [showLensMasks, setShowLensMasks] = useState(true);
  // 'raw' = what was segmented, 'blend' = the wider region the worker composites
  const [lensMaskMode, setLensMaskMode] = useState('raw');

  // Fetch rejection reasons, background mask and lens masks
  useEffect(() => {
    if (isOpen && item?.id) {
      fetchRejectionReasons();
      fetchBackgroundMask();
      fetchLensMasks();
    }
  }, [isOpen, item?.id]);

  // Lens masks were segmented on per-lens cutouts, so each one carries the
  // bbox of its cutout within the parent image as percentages. That is what
  // lets them be positioned over the frame at any display size.
  const fetchLensMasks = async () => {
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/${item.id}/lens-masks`
      );
      if (!response.ok) {
        setLensMasks([]);
        return;
      }
      const data = await response.json();
      // A mask we cannot place would land in the wrong spot; drop it rather
      // than draw it somewhere misleading.
      setLensMasks((data.masks || []).filter((m) => m.placement && m.overlay));
    } catch (err) {
      console.error('Error fetching lens masks:', err);
      setLensMasks([]);
    }
  };

  const fetchRejectionReasons = async () => {
    setFetchingReasons(true);
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/rejection-reasons`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setRejectionReasons(data.reasons || []);
    } catch (err) {
      console.error('Error fetching rejection reasons:', err);
      setError('Failed to load rejection reasons');
    } finally {
      setFetchingReasons(false);
    }
  };

  const fetchBackgroundMask = async () => {
    setFetchingMask(true);
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/${item.id}/background-mask`
      );

      if (!response.ok) {
        console.warn('No background mask available for this frame');
        setMaskData(null);
        return;
      }

      const data = await response.json();
      setMaskData(data.mask);
    } catch (err) {
      console.error('Error fetching background mask:', err);
      setMaskData(null);
    } finally {
      setFetchingMask(false);
    }
  };

  // NEW: Callback to refresh mask after update
  const handleMaskUpdated = async () => {
    console.log('Mask updated, refreshing...');
    await fetchBackgroundMask();
  };

  const handleAccept = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/accept/${item.id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (hasNext) {
        onNext();
      } else {
        onClose();
      }
    } catch (err) {
      console.error('Error accepting image:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectSubmit = async (reasonValue, description) => {
    setLoading(true);
    setError(null);

    try {
      const specialReasons = [
        'frame_lens_lip_reset',
        'frame_lens_lip_fix_pull',
        'non_transparent_lenses',
        'lens_scale_error',
        'only_one_lens_complete',
        'logo_in_final_image',
        'bbox_lens_padding_error',
        'mask_error',         
      ];

      if (specialReasons.includes(reasonValue)) {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/execute-rejection/${item.id}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              reason_value: reasonValue,
              data: {
                description: description
              }
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } else {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/reject/${item.id}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ reason: description }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      setShowRejectDialog(false);
      setSelectedReason(null);
      
      if (hasNext) {
        onNext();
      } else {
        onClose();
      }
    } catch (err) {
      console.error('Error rejecting image:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReasonSelect = (reasonValue) => {
    setSelectedReason(reasonValue);
  };

  const handleCancelRejection = () => {
    setShowRejectDialog(false);
    setSelectedReason(null);
    setError(null);
  };

  const renderReasonModal = () => {
    const modalProps = {
      onSubmit: handleRejectSubmit,
      onCancel: handleCancelRejection,
      loading,
      item,
      authenticatedFetch,
      user,
      onMaskUpdated: handleMaskUpdated,  // NEW: Pass the callback
    };

    switch (selectedReason) {
      case 'mask_error':
        return <MaskErrorModal {...modalProps} />;
      case 'lens_scale_error':
        return <LensScaleErrorModal {...modalProps} />;
      case 'only_one_lens_complete':
        return <OnlyOneLensCompleteModal {...modalProps} />;
      case 'bbox_lens_padding_error':
        return <BboxEdgePaddingError {...modalProps} />;
      case 'logo_in_final_image':
        return <LogoInFinalImageModal {...modalProps} />;
      case 'background_mask_mistake':
        return <BackgroundMaskMistakeModal {...modalProps} />;
      case 'edit_lens_mask':
        return <EditLensMaskModal {...modalProps} />;
      default:
        return null;
    }
  };

  if (!isOpen || !item) return null;

  return (
    <>
      {/* Main Review Dialog */}
      <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
        <div className="max-w-7xl w-full h-[95vh] flex flex-col">
          {/* Header */}
          <div className="bg-white rounded-t-lg px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-bold text-gray-900">Final Image Review</h2>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Frame:</span>
                <span className="text-sm font-semibold text-gray-900">{item.name || item.id}</span>
              </div>
              {maskData && (
                <button
                  onClick={() => setShowMask(!showMask)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium"
                >
                  {showMask ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  {showMask ? 'Hide Background Mask' : 'Show Background Mask'}
                </button>
              )}
              {lensMasks.length > 0 && (
                <button
                  onClick={() => setShowLensMasks(!showLensMasks)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium ${
                    showLensMasks
                      ? 'bg-sky-100 text-sky-800 hover:bg-sky-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {showLensMasks ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  {showLensMasks ? 'Hide Lens Masks' : 'Show Lens Masks'}
                  <span className="text-xs opacity-70">({lensMasks.length})</span>
                </button>
              )}
              {lensMasks.length > 0 && showLensMasks && (
                <div className="flex items-center rounded-lg border border-gray-300 overflow-hidden text-xs">
                  <button
                    onClick={() => setLensMaskMode('raw')}
                    className={`px-2.5 py-1.5 font-medium ${
                      lensMaskMode === 'raw' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    title="Exactly what the segmentation produced"
                  >
                    Segmented
                  </button>
                  <button
                    onClick={() => setLensMaskMode('blend')}
                    className={`px-2.5 py-1.5 font-medium ${
                      lensMaskMode === 'blend' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    title="Grown and feathered the way the insertion worker blends it"
                  >
                    Blend region
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Image Container */}
          <div className="flex-1 bg-gray-900 flex items-center justify-center relative">
            {hasPrevious && (
              <button
                onClick={onPrevious}
                className="absolute left-4 p-3 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full transition-colors z-10"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
            )}
            
            {hasNext && (
              <button
                onClick={onNext}
                className="absolute right-4 p-3 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full transition-colors z-10"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            )}

            <div className="relative max-w-full max-h-full">
              <img
                src={item.final_image_url}
                alt={item.name || 'Final image'}
                className="max-w-full max-h-full object-contain"
              />
              
              {/* Mask Overlay */}
              {maskData && showMask && (
                <img
                  src={maskData}
                  alt="Background mask"
                  className="absolute top-0 left-0 w-full h-full object-contain opacity-50"
                  style={{ 
                    mixBlendMode: 'multiply'
                  }}
                />
              )}

              {/* Lens mask overlay.
                  Each mask was segmented on its lens cutout, so it is placed by
                  the cutout's bbox within the parent image. Those are percentages,
                  so this stays aligned at whatever size the image renders. */}
              {showLensMasks && lensMasks.map((m, i) => {
                // placement is computed server-side the way the insertion
                // worker stamps masks: the lens bbox in the parent image,
                // grown by the cutout padding. Percentages, so it holds at
                // whatever size the image renders.
                const p = m.placement;
                if (!p || !m.mask) return null;
                return (
                  <img
                    key={m.label_id || i}
                    src={lensMaskMode === 'blend' && m.overlay_feathered ? m.overlay_feathered : m.overlay}
                    alt={p.lens_tag || `Lens mask ${i + 1}`}
                    title={p.lens_tag || undefined}
                    className="absolute pointer-events-none"
                    style={{
                      left: `${p.left_percent}%`,
                      top: `${p.top_percent}%`,
                      width: `${p.width_percent}%`,
                      height: `${p.height_percent}%`,
                      opacity: 0.5,
                    }}
                  />
                );
              })}
            </div>

            <div className="absolute top-4 left-4 bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg">
              <div className="flex items-center gap-2 text-sm">
                <ImageIcon className="w-4 h-4" />
                <span>Final Image</span>
                {fetchingMask && <Loader2 className="w-4 h-4 animate-spin" />}
              </div>
            </div>
          </div>

          {/* Action Bar */}
          <div className="bg-white rounded-b-lg px-6 py-4">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Brand:</span> {item.brand || 'N/A'}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Version:</span> {item.version || 'N/A'}
                </div>
                {item.lens_count !== null && item.lens_count !== undefined && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Lenses:</span> {item.lens_count}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowRejectDialog(true)}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  <XCircle className="w-5 h-5" />
                  Reject
                </button>
                <button
                  onClick={handleAccept}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5" />
                  )}
                  Accept
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rejection Reason Selection Dialog */}
      {showRejectDialog && !selectedReason && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            <div className="bg-red-600 text-white px-6 py-4 rounded-t-lg">
              <div className="flex items-center gap-3">
                <XCircle className="w-6 h-6" />
                <h3 className="text-lg font-bold">Select Rejection Reason</h3>
              </div>
            </div>

            <div className="p-6">
              {fetchingReasons ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                </div>
              ) : (
                <>
                  <p className="text-gray-700 mb-4">Please select a reason for rejecting this image:</p>
                  <div className="space-y-2 mb-4">
                    {rejectionReasons.map((reason) => (
                      <button
                        key={reason.value}
                        onClick={() => handleReasonSelect(reason.value)}
                        className="w-full text-left p-3 border-2 border-gray-200 hover:border-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <div className="font-medium text-gray-900">{reason.label}</div>
                        {reason.description && (
                          <div className="text-xs text-gray-600 mt-1">{reason.description}</div>
                        )}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={handleCancelRejection}
                    className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Specific Rejection Reason Modal */}
      {showRejectDialog && selectedReason && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            <div className="bg-red-600 text-white px-6 py-4 rounded-t-lg">
              <div className="flex items-center gap-3">
                <XCircle className="w-6 h-6" />
                <h3 className="text-lg font-bold">Reject Image</h3>
              </div>
            </div>
            {error && (
              <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            {renderReasonModal()}
          </div>
        </div>
      )}
    </>
  );
}

function RevisionGlassesFramePage() {
  const { authenticatedFetch } = useAuth();
  
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [readiness, setReadiness] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

  const observerTarget = useRef(null);
  const pageSize = 20;

  const fetchItems = useCallback(async (page) => {
    if (loading) return;
    
    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/revision/glasses-frame/list?page=${page}&page_size=${pageSize}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (page === 1) {
        setItems(data.items || []);
      } else {
        setItems(prev => [...prev, ...(data.items || [])]);
      }
      
      setTotalCount(data.total_count || 0);
      setReadiness(data.readiness || null);
      setTotalPages(data.total_pages || 0);
      setHasMore(page < (data.total_pages || 0));
      setCurrentPage(page);
      
    } catch (err) {
      console.error('Error fetching items:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, loading]);

  useEffect(() => {
    fetchItems(1);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchItems(currentPage + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, currentPage, fetchItems]);


  // Preload visible images for caching
useEffect(() => {
  if (items.length > 0) {
    const imageUrls = items.map(item => item.final_image_url).filter(Boolean);
    preloadImages(imageUrls);
  }
}, [items]);

// Preload adjacent images when dialog opens for smooth navigation
useEffect(() => {
  if (selectedItemIndex !== null && items.length > 0) {
    const adjacentUrls = [];
    
    // Preload previous image
    if (selectedItemIndex > 0) {
      adjacentUrls.push(items[selectedItemIndex - 1].final_image_url);
    }
    
    // Preload next image
    if (selectedItemIndex < items.length - 1) {
      adjacentUrls.push(items[selectedItemIndex + 1].final_image_url);
    }
    
    // Preload next 2 images for smoother experience
    if (selectedItemIndex < items.length - 2) {
      adjacentUrls.push(items[selectedItemIndex + 2].final_image_url);
    }
    
    preloadImages(adjacentUrls.filter(Boolean));
  }
}, [selectedItemIndex, items]);
  const handleRefresh = () => {
    setItems([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchItems(1);
  };

  const handleImageClick = (index) => {
    setSelectedItemIndex(index);
    setReviewDialogOpen(true);
  };

  const handleNext = () => {
    if (selectedItemIndex < items.length - 1) {
      setSelectedItemIndex(selectedItemIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (selectedItemIndex > 0) {
      setSelectedItemIndex(selectedItemIndex - 1);
    }
  };

  const handleCloseDialog = () => {
    setReviewDialogOpen(false);
    setSelectedItemIndex(null);
    handleRefresh();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'accepted':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
            <CheckCircle className="w-3 h-3" />
            Accepted
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            <XCircle className="w-3 h-3" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
            <AlertTriangle className="w-3 h-3" />
            Pending Review
          </span>
        );
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <ImageIcon className="w-8 h-8 text-indigo-600" />
                Final Image Revision
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {totalCount} total frames pending review
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="text-sm font-medium">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="px-6 py-6">
          <ReadinessBar readiness={readiness} unitLabel="frames" />

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Error loading items</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {items.length === 0 && !loading ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              {/* Nothing to check is not the same as nothing left to do - say
                  which one it is. */}
              <div className="flex flex-col items-center gap-3">
                {readiness?.waiting > 0 ? (
                  <>
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                      <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Nothing ready to check yet</h3>
                    <p className="text-gray-600">
                      {readiness.waiting.toLocaleString()} {readiness.waiting === 1 ? 'frame is' : 'frames are'}{' '}
                      still being processed and will appear here once their final image is produced.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">All images reviewed!</h3>
                    <p className="text-gray-600">There are no pending images for final revision</p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {items.map((item, index) => (
                  <div
                    key={item.id}
                    className={`bg-white rounded-lg shadow-sm border-2 overflow-hidden transition-all ${
                      item.review_status === 'pending'
                        ? 'border-gray-200 hover:border-indigo-500 hover:shadow-lg cursor-pointer'
                        : 'border-gray-300 opacity-60 cursor-not-allowed'
                    }`}
                    onClick={() => item.review_status === 'pending' && handleImageClick(index)}
                  >
                    <div className="aspect-square bg-gray-100 relative overflow-hidden group">
                      <img
                        src={item.final_image_url}
                        alt={item.name || 'Final image'}
                        className="w-full h-full object-contain"
                      />
                      
                      {item.review_status === 'pending' && (
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <ZoomIn className="w-8 h-8 text-white" />
                          </div>
                        </div>
                      )}

                      <div className="absolute top-2 right-2">
                        {getStatusBadge(item.review_status)}
                      </div>
                    </div>

                    <div className="p-3">
                      <h3 className="font-semibold text-sm text-gray-900 truncate mb-1">
                        {item.name || `Frame ${item.id.slice(0, 8)}`}
                      </h3>
                      <div className="flex items-center justify-between text-xs text-gray-600">
                        <span>{item.brand || 'N/A'}</span>
                        {item.lens_count !== null && item.lens_count !== undefined && (
                          <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full font-medium">
                            {item.lens_count}L
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div ref={observerTarget} className="mt-6 text-center">
                {loading && (
                  <div className="flex items-center justify-center gap-2">
                    <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
                    <span className="text-sm text-gray-600">Loading more...</span>
                  </div>
                )}
                {!hasMore && items.length > 0 && (
                  <div className="text-sm text-gray-500">
                    Showing all {totalCount} items • Page {currentPage} of {totalPages}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {selectedItemIndex !== null && (
        <ImageReviewDialog
          isOpen={reviewDialogOpen}
          onClose={handleCloseDialog}
          item={items[selectedItemIndex]}
          onNext={handleNext}
          onPrevious={handlePrevious}
          hasNext={selectedItemIndex < items.length - 1}
          hasPrevious={selectedItemIndex > 0}
        />
      )}
    </div>
  );
}

export default RevisionGlassesFramePage;